import XMarkIcon from '@heroicons/react/20/solid/XMarkIcon.js';
import React from 'react';
import {
  Button as AriaButton,
  type QueuedToast,
  Text as AriaText,
  UNSTABLE_Toast as AriaToast,
  UNSTABLE_ToastContent as AriaToastContent,
  UNSTABLE_ToastRegion as AriaToastRegion,
} from 'react-aria-components';

import { tv } from '../../tv';
import { Button, buttonStyles } from '../button/button';
import { STATUS_ICONS } from '../callout/status-icons';
import { surfacedSurface } from '../overlay/overlay-portal';
import { Spinner } from '../spinner';
import { toastCenter, type ToastItem } from './toast-center';

/**
 * A brief notice about something that just happened, stacked at the bottom of
 * the viewport and gone on its own. A SURFACED overlay (overlays.md): the
 * status is the glyph and its colour on a plain surface, never a coloured
 * slab. The region is mounted once per root and renders whatever the toast
 * centre holds; hovering or focusing it holds every clock (toast.md).
 */

const regionStyles = tv({
  base: [
    'ck:fixed ck:bottom-4 ck:left-1/2 ck:-translate-x-1/2 ck:z-toast',
    'ck:flex ck:w-full ck:max-w-sm ck:flex-col ck:items-stretch ck:gap-2 ck:px-4',
    'ck:outline-none ck:focus-visible:focus-ring',
  ],
});

const toastStyles = tv({
  slots: {
    root: [
      surfacedSurface,
      'ck:flex ck:w-full ck:items-start ck:gap-2 ck:p-3 ck:text-sm ck:animate-enter',
      'ck:outline-none ck:focus-visible:focus-ring',
    ],
    /* Callout's glyph on the indicator step (9), not its text step: on a plain
     * surface the glyph is the only colour, on Callout's wash it reads with the text. */
    icon: 'ck:size-5 ck:shrink-0',
    body: 'ck:flex ck:min-w-0 ck:flex-1 ck:flex-col ck:gap-1',
    title: 'ck:font-medium ck:text-text-default',
    description: 'ck:text-text-muted',
    /* -my-1 lets a 24px control overhang the padding so its glyph aligns with the text. */
    action: 'ck:-my-1 ck:shrink-0',
    close: 'ck:-my-1 ck:-mr-1 ck:shrink-0',
  },
  variants: {
    status: {
      info: { icon: 'ck:text-info' },
      success: { icon: 'ck:text-success' },
      warning: { icon: 'ck:text-warning' },
      error: { icon: 'ck:text-error' },
      loading: { icon: 'ck:text-text-muted' },
    },
  },
});

const CloseButton = ({ label, className }: { label: string; className: string }) => {
  /* React Aria's `close` slot wants a slotted Button; IconButton cannot sit in
   * a slot, so its ghost square is drawn from the shared recipe. */
  const { root, content, icon } = buttonStyles({
    variant: 'ghost',
    tone: 'neutral',
    size: 'sm',
    shape: 'square',
    pending: false,
    ariaDisabled: false,
  });
  return (
    <span className={className}>
      <AriaButton slot="close" aria-label={label} className={root()}>
        <span className={content()}>
          <XMarkIcon aria-hidden="true" focusable="false" className={icon()} />
        </span>
      </AriaButton>
    </span>
  );
};

const ToastCard = ({
  toast,
  dismissLabel,
}: {
  toast: QueuedToast<ToastItem>;
  dismissLabel: string;
}) => {
  const content = React.useSyncExternalStore(toast.content.subscribe, toast.content.get);
  const { root, icon, body, title, description, action, close } = toastStyles({
    status: content.status,
  });
  const StatusIcon = content.status === 'loading' ? null : STATUS_ICONS[content.status];

  return (
    <AriaToast toast={toast} className={root()} data-component="toast" data-status={content.status}>
      {StatusIcon ? (
        <StatusIcon className={icon()} aria-hidden />
      ) : (
        <span className={icon()}>
          <Spinner size="sm" />
        </span>
      )}
      <AriaToastContent className={body()}>
        <AriaText slot="title" className={title()}>
          {content.title}
        </AriaText>
        {content.description ? (
          <AriaText slot="description" className={description()}>
            {content.description}
          </AriaText>
        ) : null}
      </AriaToastContent>
      {content.action ? (
        <span className={action()}>
          <Button
            size="sm"
            variant="outline"
            onPress={() => {
              content.action?.onPress();
              toastCenter.close(toast.content.id);
            }}
          >
            {content.action.label}
          </Button>
        </span>
      ) : null}
      <CloseButton label={dismissLabel} className={close()} />
    </AriaToast>
  );
};

export interface ToastRegionProps {
  /** The landmark's name: "Notifications". */
  label?: string;
  /** The close control's name on every toast. */
  dismissLabel?: string;
}

const preserveToastRegion = (region: HTMLDivElement | null) => {
  // React Aria filters aria-live props; Chakra preserves aria-live nodes when hiding background content.
  region?.setAttribute('aria-live', 'off');
};

export const ToastRegion = ({
  label = 'Notifications',
  dismissLabel = 'Dismiss',
}: ToastRegionProps) => (
  <AriaToastRegion
    ref={preserveToastRegion}
    queue={toastCenter.queue}
    aria-label={label}
    className={regionStyles()}
    data-component="toast-region"
  >
    {({ toast }) => <ToastCard toast={toast} dismissLabel={dismissLabel} />}
  </AriaToastRegion>
);
